/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-10-22
 */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import {
  IconComponent,
  IconName,
  NebulaSessionContextChipComponent,
  NebulaSessionContextService,
} from 'nebula-ui-kit';
import { SystemSyncTopbarDateService, TranslatePipe } from '@nebula-shared/data-access';

/** Mismo corte que el layout usa para el menu flotante. */
export const CONTEXTO_PANTALLA_ANGOSTA = '(max-width: 767px)';

/** Icono del boton «Contexto» (Carlos, 22-oct-2026; catalogo de iconos del tablero, 2b). */
export const CONTEXTO_ICONO: IconName = 'Calendar';

/** Separacion entre la miga y el contexto, y margen derecho del contexto (layout.component.scss). */
export const CONTEXTO_HUECO_PX = 12 + 16;

/**
 * Histeresis para volver de «Contexto» a los chips: deben sobrar estos pixeles. Sin ella, en el
 * ancho limite alternaria entre chips y boton.
 */
export const CONTEXTO_HISTERESIS_PX = 24;

/**
 * Ancho que piden los tramos de la miga del kit, no el sitio que le toca: lib-breadcrumb se estira
 * para llenar la barra (flex: 1), asi que su scrollWidth crece con el sitio y la decision se quedaba
 * pegada. Se suman los rellenos del componente (su ancho menos el del nav) y el contenido (el ol).
 *
 * <p>Limite conocido: si el kit cambia la estructura y no hay nav u ol, vuelve al scrollWidth, que
 * es el sitio asignado y deja el boton pegado otra vez (el defecto de la 0.4.0).</p>
 *
 * @param miga el lib-breadcrumb de la barra, o nulo
 * @returns el ancho natural en pixeles
 */
export function anchoNaturalDeLaMiga(miga: HTMLElement | null): number {
  const nav = miga?.querySelector('nav');
  const tramos = nav?.querySelector('ol');
  if (!miga || !nav || !tramos) return miga?.scrollWidth ?? 0;
  return miga.offsetWidth - nav.clientWidth + tramos.scrollWidth;
}

/**
 * Si la dependencia y la fecha se agrupan en «Contexto» porque no caben junto a la miga.
 *
 * @param agrupado    si ahora estan agrupadas
 * @param necesidad   ancho que piden la miga entera, los chips y su separacion
 * @param ancho       ancho de la barra
 * @returns si deben quedar agrupadas
 */
export function debeAgrupar(agrupado: boolean, necesidad: number, ancho: number): boolean {
  return agrupado ? necesidad > ancho - CONTEXTO_HISTERESIS_PX : necesidad > ancho;
}

/**
 * Contexto de la sesion en la barra de la miga (perfil 3.0): la dependencia de la ruta activa,
 * con su selector, y la fecha del sistema. Si estan los dos y no caben junto a la miga (pantallas
 * angostas o monitores en vertical), se agrupan en un boton «Contexto» que abre un panel; si solo hay
 * uno, va suelto (Carlos, 22-oct-2026).
 *
 * <p>La dependencia es el mismo chip del kit que antes iba en la cabecera: solo se ve si la ruta la
 * declara. La fecha sale si la ruta la muestra.</p>
 */
@Component({
  selector: 'app-contexto-sesion',
  templateUrl: './contexto-sesion.component.html',
  styleUrls: ['./contexto-sesion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, NebulaSessionContextChipComponent, TranslatePipe],
})
export class ContextoSesionComponent implements AfterViewInit, OnDestroy {
  private readonly contexto = inject(NebulaSessionContextService);
  private readonly fechas = inject(SystemSyncTopbarDateService);
  private readonly anfitrion = inject<ElementRef<HTMLElement>>(ElementRef);

  /** La ruta activa muestra la fecha del sistema. */
  readonly mostrarFecha = input(false);

  readonly icono = CONTEXTO_ICONO;
  readonly abierto = signal(false);

  readonly angosto = toSignal(
    inject(BreakpointObserver)
      .observe(CONTEXTO_PANTALLA_ANGOSTA)
      .pipe(map((estado) => estado.matches)),
    { initialValue: false },
  );

  /** La fuente de la ruta activa: la misma que pinta el chip del kit, sin otra consulta. */
  readonly fuente = this.contexto.source;
  /** La ruta declara una dependencia con opciones. */
  readonly conDependencia = computed(() => !!this.fuente()?.available());
  /** Nombre de la dependencia activa, o nulo si no hay. */
  readonly dependenciaActiva = computed(() => this.fuente()?.active()?.name ?? null);
  readonly fecha = computed(() => this.fechas.fechaSistemaFormatted() || '');
  readonly conFecha = computed(() => this.mostrarFecha() && !!this.fecha());
  readonly visible = computed(() => this.conDependencia() || this.conFecha());

  /** La miga y los chips no caben juntos en la barra. */
  readonly sinSitio = signal(false);

  /** El boton solo agrupa cuando estan los dos: en angosto, o cuando no caben junto a la miga. */
  readonly enBoton = computed(
    () => this.conDependencia() && this.conFecha() && (this.angosto() || this.sinSitio()),
  );

  /**
   * La fecha va sola y no cabe junto a la miga: queda solo su ficha, con el valor en el tooltip y
   * en el aria-label, como el chip del kit en angosto.
   */
  readonly fechaCompacta = computed(
    () => !this.conDependencia() && this.conFecha() && this.sinSitio(),
  );

  /**
   * Ancho de los chips, medido la ultima vez que se vieron completos; con el boton o con la ficha
   * sola no se pueden medir y se compara contra este.
   */
  private anchoChips = 0;
  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    // Cambiar de ruta cambia la miga, y llegar la dependencia o la fecha cambia los chips.
    inject(Router)
      .events.pipe(
        filter((evento) => evento instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe(() => requestAnimationFrame(() => this.medir()));
    effect(() => {
      this.conDependencia();
      this.conFecha();
      requestAnimationFrame(() => this.medir());
    });
  }

  /** Mismo patron que el contenedor de pestañas: se observa la barra y se mide en el siguiente frame. */
  ngAfterViewInit(): void {
    const barra = this.anfitrion.nativeElement.parentElement;
    if (!barra) return;
    this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(() => this.medir()));
    this.resizeObserver.observe(barra, { box: 'border-box' });
    requestAnimationFrame(() => this.medir());
  }

  /** Decide si los chips caben: el ancho natural de la miga mas los chips, contra la barra. */
  medir(): void {
    const anfitrion = this.anfitrion.nativeElement;
    const barra = anfitrion.parentElement;
    if (!barra) return;
    if (!this.enBoton() && !this.fechaCompacta()) this.anchoChips = anfitrion.offsetWidth;
    // Sin chips medidos no hay con que comparar: se muestran para medirlos en el siguiente frame.
    if (!this.anchoChips) {
      this.sinSitio.set(false);
      return;
    }
    const miga = barra.querySelector('lib-breadcrumb') as HTMLElement | null;
    const necesidad = anchoNaturalDeLaMiga(miga) + CONTEXTO_HUECO_PX + this.anchoChips;
    this.sinSitio.set(debeAgrupar(this.sinSitio(), necesidad, barra.clientWidth));
  }

  alternar(): void {
    this.abierto.update((abierto) => !abierto);
  }

  @HostListener('document:keydown.escape')
  cerrar(): void {
    this.abierto.set(false);
  }

  /** Un clic fuera del boton y su panel lo cierra; el panel de la dependencia esta dentro. */
  @HostListener('document:click', ['$event'])
  alClicFuera(evento: Event): void {
    if (this.abierto() && !this.anfitrion.nativeElement.contains(evento.target as Node)) {
      this.cerrar();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }
}
